import { Unplug } from 'lucide-react';
import { useLocation } from 'react-router';
import { useI18nContext } from '@/i18n/i18n-react';
import { useFeatureFlags } from '@/hooks/use-feature-flags';
import { useLocalSettings } from '@/hooks/use-local-settings';
import { useTenant } from '@/hooks/use-tenant';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { ExternalLinkButton } from '@/components/external-link-button';

export function IntegrationUpdateModal() {
  const tenant = useTenant();
  const { LL } = useI18nContext();
  const location = useLocation();
  const featureFlags = useFeatureFlags();
  const { localSettings, setLocalSettings } = useLocalSettings();

  const isDismissed =
    localSettings.integrationUpdateModalDismissedTenantSlugs[tenant.slug] ===
    true;

  const canSetUpIntegration =
    (tenant.ecommercePlatform === 'nuvemshop' ||
      tenant.ecommercePlatform === 'shopify') &&
    tenant.activateIntegrationUrl != null;

  const shouldShow =
    tenant.isBillingSetUp &&
    !tenant.isStoreIntegrationConfigured &&
    !tenant.isAppUnderConstruction &&
    featureFlags.data?.showIntegrationUpdateMessage === true &&
    !location.pathname.includes('/integrations/') &&
    !isDismissed &&
    !featureFlags.isLoading;

  if (!shouldShow) {
    return null;
  }

  const handleIgnore = () => {
    setLocalSettings({
      ...localSettings,
      integrationUpdateModalDismissedTenantSlugs: {
        ...localSettings.integrationUpdateModalDismissedTenantSlugs,
        [tenant.slug]: true,
      },
    });
  };

  return (
    <AlertDialog open>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="mb-2 flex justify-center">
            <span className="rounded-full bg-blue-100 p-3 text-blue-700">
              <Unplug className="size-6" />
            </span>
          </div>
          <AlertDialogTitle className="text-center text-xl">
            {LL.integrationUpdateModal.title()}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-center">
            {LL.integrationUpdateModal.description()}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
          {canSetUpIntegration && (
            <ExternalLinkButton
              className="w-full"
              href={tenant.activateIntegrationUrl ?? ''}
            >
              {LL.integrationUpdateModal.setupIntegrationButton()}
            </ExternalLinkButton>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-muted-foreground"
            onClick={handleIgnore}
          >
            {LL.integrationUpdateModal.ignoreButton()}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
